import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { GetJobOfferCandidate } from 'src/app/models/job-offer/get-job-offer-candidate';
import { DropDownKeywords, DropdownOption } from 'src/app/models/shared/dropdown-option';
import { DropdownService } from 'src/app/services/dropdown.service';
import { JobOfferService } from 'src/app/services/job-offer.service';
import { environment } from 'src/environments/environment';
import { DropdownComponent } from "../../shared/dropdown/dropdown.component";
import { FileStorageService } from 'src/app/services/file-storage.service';
import { AuthService } from 'src/app/services/auth.service';
import { PaginationComponent } from 'src/app/components/pagination/pagination.component';
import { ViewTrackingService, ViewType } from 'src/app/services/view-tracking.service';
import { ViewCountBaseService } from 'src/app/services/view-count-base.service';

@Component({
    selector: 'app-job-search',
    templateUrl: './job-search.component.html',
    styleUrl: './job-search.component.css',
    standalone: true,
    imports: [CommonModule, RouterLink, FormsModule, DropdownComponent, PaginationComponent]
})
export class JobSearchComponent implements OnInit {
  jobOffers = signal<GetJobOfferCandidate[]>([]);
  page = 1;
  pageSize = 5;
  totalItems = 0;
  totalPages = 0;
  isLoading = signal(false);
  isUserLoggedIn = signal(false);
  
  // Tracking properties
  ViewType = ViewType;
  viewCounts = signal<{ [key: number]: number }>({});
  
  url = environment.api.url;
  
  provinces: DropdownOption[] = [];
  departments: DropdownOption[] = [];
  positions: DropdownOption[] = [];
  searchText: string = '';
  filter: {
    stateId: number | undefined,
    departmentId: number | undefined,
    positionId: number | undefined
  } = {
    stateId: undefined,
    departmentId: undefined,
    positionId: undefined
  };
  
  // Page size options
  pageSizeOptions: number[] = [5, 10, 20, 50, 100];

  constructor(
    private jobOfferService: JobOfferService,
    private dropdownService: DropdownService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private docService: FileStorageService,
    private authService: AuthService,
    private viewTrackingService: ViewTrackingService,
    private viewCountService: ViewCountBaseService
  ) { 
    this.isUserLoggedIn.set(this.authService.isValidToken());
  }

  ngOnInit(): void {
    this.getDropdownOptions().then(() => {
      this.activatedRoute.queryParams.subscribe(params => {
        let filtersChanged = false;
        
        if (params['stateId'] != this.filter.stateId) {
          this.filter.stateId = +params['stateId'] || undefined;
          filtersChanged = true;
        }
        
        if (params['departmentId'] != this.filter.departmentId) {
          this.filter.departmentId = +params['departmentId'] || undefined;
          filtersChanged = true;
        }
        
        if (params['positionId'] != this.filter.positionId) {
          this.filter.positionId = +params['positionId'] || undefined;
          filtersChanged = true;
        }

        if (params['search'] != this.searchText) {
          this.searchText = params['search'] ;
          filtersChanged = true;
        }
        
        if (filtersChanged) {
          this.getJobOffers();
        } else if (!this.jobOffers().length) {
          this.getJobOffers();
        }
      });
    });
  }

  getDropdownOptions(): Promise<void> {
    return new Promise<void>((resolve) => {
      let completedCalls = 0;
      const totalCalls = 3;
      
      const checkAllCompleted = () => {
        completedCalls++;
        if (completedCalls === totalCalls) {
          resolve();
        }
      };
      
      this.dropdownService.GetOptions(DropDownKeywords.Provinces).subscribe({
        next: res => {
          this.provinces = res;
          checkAllCompleted();
        },
        error: () => checkAllCompleted()
      });
      
      this.dropdownService.GetOptions(DropDownKeywords.Departments).subscribe({
        next: res => {
          this.departments = res;
          checkAllCompleted();
        },
        error: () => checkAllCompleted()
      });
      
      this.dropdownService.GetOptions(DropDownKeywords.Positions).subscribe({
        next: res => {
          this.positions = res;
          checkAllCompleted();
        },
        error: () => checkAllCompleted()
      });
    });
  }

  getJobOffers() {
    this.isLoading.set(true);
    this.jobOfferService.getJobOffersCandidate({
      page: this.page,
      pageSize: this.pageSize,
      appliedOffers: false,
      stateId: this.filter.stateId,
      departmentId: this.filter.departmentId,
      positionId: this.filter.positionId,
      search: this.searchText
    }).subscribe({
      next: res => {
        const jobOffers = res?.items.map((x) => {
          return {
            ...x,
            logoUrl: x.logoFileId ? this.docService.getFileUrl(`${x.logoFileId}`) : null
          }
        }) ?? [];
        this.isLoading.set(false);
        this.totalItems = res?.totalItems ?? 0;
        this.jobOffers.set(jobOffers);
        this.totalPages = res?.totalPages ?? 0;
        
        // Cargar conteos de vistas después de obtener las ofertas
        this.loadViewCounts();
      },
      error: () => {
        this.isLoading.set(false);
      }
    });
  }
  
  onSearch() {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        stateId: this.filter.stateId ?? null,
        departmentId: this.filter.departmentId ?? null,
        positionId: this.filter.positionId ?? null,
        search: this.searchText ?? null
      },
      queryParamsHandling: 'merge'
    });
    
    this.page = 1;
    this.getJobOffers();
  }

  onPageChange(page: number) {
    if (page > 0 && page <= this.totalPages) {
      this.page = page;
      this.getJobOffers();
    }
  }

  onPageSizeChange(size: number) {
    this.pageSize = size;
    this.page = 1;
    this.getJobOffers();
  }
  
  clearFilters() {
    this.filter = {
      stateId: 0,
      departmentId: 0,
      positionId: 0
    };
    this.searchText = '';
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {}
    });
    this.getJobOffers();
  }

  getViewCount(jobOfferId: number): number {
    return this.viewCountService.getViewCount(this.viewCounts, jobOfferId);
  }

  loadViewCounts() {
    this.viewCountService.loadViewCounts(this.jobOffers(), ViewType.JobOffer, this.viewCounts);
  }
}
